import React, { useState, useEffect, useRef } from 'react';
import { Mic, Square, Sparkles, Download, User, AlertCircle, CheckCircle, Edit2, Check, Share2, X } from 'lucide-react';

const MedicalFeedbackApp = () => {
  const [sessionStarted, setSessionStarted] = useState(false);
  const [enrollmentStep, setEnrollmentStep] = useState(null); // null | 'overview' | 'faculty' | 'trainee'
  const [isEnrollmentRecording, setIsEnrollmentRecording] = useState(false);
  const [enrollmentCountdown, setEnrollmentCountdown] = useState(6);
  const [enrollmentDone, setEnrollmentDone] = useState({ faculty: false, trainee: false });
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcribingProgress, setTranscribingProgress] = useState(0);
  const [generatingProgress, setGeneratingProgress] = useState(0);
  const [voiceActive, setVoiceActive] = useState(false);
  const [waveformBars, setWaveformBars] = useState(Array(20).fill(2));
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [facultyName, setFacultyName] = useState('');
  const [facultyPronouns, setFacultyPronouns] = useState('');
  const [traineeName, setTraineeName] = useState('');
  const [traineePronouns, setTraineePronouns] = useState('');
  const [trainingLevel, setTrainingLevel] = useState('');
  const [transcript, setTranscript] = useState([]);
  const [enhancedSummary, setEnhancedSummary] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('transcript');
  const [isFinalized, setIsFinalized] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [editedPlusNarrative, setEditedPlusNarrative] = useState('');
  const [editedDeltaNarrative, setEditedDeltaNarrative] = useState('');
  const [plusEditMode, setPlusEditMode] = useState(false);
  const [deltaEditMode, setDeltaEditMode] = useState(false);
  const [showAttestModal, setShowAttestModal] = useState(false);
  const [transcribingPhase, setTranscribingPhase] = useState(''); // 'deepgram' | 'cleaning' | ''
  const rawTranscriptRef = useRef([]); // stores raw Deepgram output for developer comparison
  const transcriptEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const enrollmentMimeTypeRef = useRef('audio/webm');
  const enrollmentCountdownRef = useRef(null);
  const streamRef = useRef(null);
  const wakeLockRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const durationTimerRef = useRef(null);

  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript]);

  // Inject waveform keyframe animations
  useEffect(() => {
    const id = 'waveform-keyframes';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
      @keyframes spin { to { transform: rotate(360deg); } }
      @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.3; } }
    `;
    document.head.appendChild(style);
  }, []);

  // Transcribing progress bar — fills over ~35s to cover both Deepgram + Call 0
  // Holds at 90% until both are done, then snaps to 100%
  useEffect(() => {
    if (!isTranscribing) { setTranscribingProgress(0); return; }
    setTranscribingProgress(0);
    const duration = 35000; // ~20s Deepgram + ~15s Call 0
    const interval = 100;
    const step = 90 / (duration / interval);
    const timer = setInterval(() => {
      setTranscribingProgress(p => Math.min(p + step, 90));
    }, interval);
    return () => clearInterval(timer);
  }, [isTranscribing]);

  // Snap to 100% when transcribing completes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isTranscribing && transcribingProgress > 0) {
      setTranscribingProgress(100);
    }
  }, [isTranscribing]);

  // Generating progress bar — fills over ~30s, holds at 90% until done
  useEffect(() => {
    if (!isGeneratingSummary) { setGeneratingProgress(0); return; }
    setGeneratingProgress(0);
    const duration = 30000;
    const interval = 100;
    const step = 90 / (duration / interval);
    const timer = setInterval(() => {
      setGeneratingProgress(p => Math.min(p + step, 90));
    }, interval);
    return () => clearInterval(timer);
  }, [isGeneratingSummary]);

  // Snap to 100% when generating completes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isGeneratingSummary && generatingProgress > 0) {
      setGeneratingProgress(100);
    }
  }, [isGeneratingSummary]);

  // ── Waveform + voice activity analyser ──────────────────────────────────
  const startAnalyser = (stream) => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 64;
    analyser.smoothingTimeConstant = 0.7;
    const source = ctx.createMediaStreamSource(stream);
    source.connect(analyser);
    audioContextRef.current = ctx;
    analyserRef.current = analyser;
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    const VOICE_THRESHOLD = 20;
    const tick = () => {
      analyser.getByteFrequencyData(dataArray);
      const bars = Array.from({ length: 20 }, (_, i) => {
        const idx = Math.floor((i / 20) * dataArray.length);
        return Math.max(2, (dataArray[idx] / 255) * 48);
      });
      setWaveformBars(bars);
      const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
      setVoiceActive(avg > VOICE_THRESHOLD);
      animationFrameRef.current = requestAnimationFrame(tick);
    };
    tick();
  };

  const stopAnalyser = () => {
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    if (audioContextRef.current) audioContextRef.current.close();
    audioContextRef.current = null;
    analyserRef.current = null;
    setWaveformBars(Array(20).fill(2));
    setVoiceActive(false);
  };

  const startSession = () => {
    if (facultyName && traineeName && trainingLevel) setEnrollmentStep('overview');
  };

  // ── Enrollment recording ─────────────────────────────────────────────────
  const startEnrollmentRecording = async (speaker) => {
    // Faculty enrollment starts the one persistent MediaRecorder that runs
    // all the way through to the end of the session. One recorder = one WebM
    // container header = no concatenation = no parser issues for Deepgram.
    // Trainee enrollment is UX only — the recorder is already running.
    if (speaker === 'trainee') {
      // Recorder is already running from faculty enrollment.
      // Just run the countdown UI — no audio action needed.
      setIsEnrollmentRecording(true);
      setEnrollmentCountdown(6);
      let count = 6;
      enrollmentCountdownRef.current = setInterval(() => {
        count--;
        setEnrollmentCountdown(count);
        if (count <= 0) {
          clearInterval(enrollmentCountdownRef.current);
          setIsEnrollmentRecording(false);
          setEnrollmentDone(prev => ({ ...prev, trainee: true }));
          setTimeout(() => {
            setEnrollmentStep(null);
            setSessionStarted(true);
          }, 600);
        }
      }, 1000);
      return;
    }

    // Faculty enrollment — start the one persistent recorder
    try {
      setError(null);
      audioChunksRef.current = [];

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, sampleRate: 48000, channelCount: 1 }
      });
      streamRef.current = stream;
      startAnalyser(stream);

      const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4']
        .find(m => MediaRecorder.isTypeSupported(m)) || '';

      const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      mediaRecorderRef.current = mediaRecorder;
      // All audio — enrollment and session — flows into the same chunks array
      mediaRecorder.ondataavailable = (e) => { if (e.data?.size > 0) audioChunksRef.current.push(e.data); };
      mediaRecorder.start(100);

      setIsEnrollmentRecording(true);
      setEnrollmentCountdown(6);
      setEnrollmentDone(prev => ({ ...prev, faculty: false }));

      let count = 6;
      enrollmentCountdownRef.current = setInterval(() => {
        count--;
        setEnrollmentCountdown(count);
        if (count <= 0) {
          clearInterval(enrollmentCountdownRef.current);
          setIsEnrollmentRecording(false);
          setEnrollmentDone(prev => ({ ...prev, faculty: true }));
          // Recorder keeps running — advance to trainee enrollment UI
          setTimeout(() => setEnrollmentStep('trainee'), 600);
        }
      }, 1000);

    } catch (err) {
      setError(`Failed to start enrollment recording: ${err.message}`);
      setIsEnrollmentRecording(false);
    }
  };

  // ── Record-then-transcribe ───────────────────────────────────────────────
  const startRecording = async () => {
    // If the recorder is already running from enrollment, just update UI state.
    // The audio stream is already capturing — no need to start a new recorder.
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      if ('wakeLock' in navigator) {
        navigator.wakeLock.request('screen').then(wl => { wakeLockRef.current = wl; }).catch(() => {});
      }
      setIsRecording(true);
      setRecordingDuration(0);
      durationTimerRef.current = setInterval(() => setRecordingDuration(d => d + 1), 1000);
      return;
    }

    // Fallback: started directly without enrollment (e.g. re-record scenario)
    try {
      setError(null);
      audioChunksRef.current = [];

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, sampleRate: 48000, channelCount: 1 }
      });
      streamRef.current = stream;
      startAnalyser(stream);

      if ('wakeLock' in navigator) {
        navigator.wakeLock.request('screen').then(wl => { wakeLockRef.current = wl; }).catch(() => {});
      }

      const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4']
        .find(m => MediaRecorder.isTypeSupported(m)) || '';
      const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.ondataavailable = (e) => { if (e.data?.size > 0) audioChunksRef.current.push(e.data); };
      mediaRecorder.start(1000);

      setIsRecording(true);
      setRecordingDuration(0);
      durationTimerRef.current = setInterval(() => setRecordingDuration(d => d + 1), 1000);

    } catch (err) {
      setError(`Failed to start recording: ${err.message}`);
    }
  };

  const stopRecording = async () => {
    setIsRecording(false);
    setIsTranscribing(true);
    setTranscribingPhase('deepgram');
    setError(null);
    if (durationTimerRef.current) clearInterval(durationTimerRef.current);
    stopAnalyser();
    // NOTE: wake lock is intentionally kept alive here — released after transcribing completes

    try {
      await new Promise((resolve) => {
        const mr = mediaRecorderRef.current;
        if (!mr || mr.state === 'inactive') { resolve(); return; }
        mr.onstop = resolve;
        mr.stop();
      });
      if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
      if (audioChunksRef.current.length === 0) throw new Error('No audio recorded');

      const mimeType = mediaRecorderRef.current?.mimeType || 'audio/webm';
      const deepgramMime = mimeType.split(';')[0] || 'audio/webm';

      console.log('=== BLOB CONSTRUCTION DIAGNOSTIC ===');
      console.log('Total chunks (enrollment + session):', audioChunksRef.current.length,
        '| total bytes:', audioChunksRef.current.reduce((s, c) => s + c.size, 0));

      const audioBlob = new Blob(audioChunksRef.current, { type: deepgramMime });
      console.log('Audio blob ready:', audioBlob.size, 'bytes, type:', deepgramMime);
      if (audioBlob.size < 1000) throw new Error('Audio recording too short or empty');

      // ── STEP 1: Send to Deepgram ──────────────────────────────────────────
      const rawParsed = await transcribeAudio(audioBlob, deepgramMime);

      // ── STEP 2: Send raw transcript to Call 0 for cleanup ─────────────────
      setTranscribingPhase('cleaning');
      const rawTranscriptString = rawParsed.map(t =>
        `${t.speaker === 'faculty' ? facultyName : traineeName}: ${t.text}`
      ).join('\n\n');

      // Store raw for developer comparison
      rawTranscriptRef.current = rawParsed;

      const cleanRes = await fetch('/api/clean-transcript', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rawTranscript: rawTranscriptString,
          facultyName,
          facultyPronouns,
          traineeName,
          traineePronouns,
          trainingLevel,
        })
      });

      if (!cleanRes.ok) {
        // Call 0 failed — fall back to raw Deepgram output, still usable
        console.warn('Call 0 failed — displaying raw Deepgram transcript');
        setTranscript(rawParsed);
      } else {
        const cleanData = await cleanRes.json();

        // ── DEVELOPER COMPARISON LOG ─────────────────────────────────────────
        // Only visible in browser DevTools console — not shown to users
        console.group('%c=== CALL 0 COMPARISON (Developer View) ===', 'color: #6366f1; font-weight: bold; font-size: 13px');
        console.log('%c--- RAW DEEPGRAM OUTPUT ---', 'color: #ef4444; font-weight: bold');
        console.log(rawTranscriptString);
        console.log('%c--- CALL 0 CLEANED OUTPUT ---', 'color: #16a34a; font-weight: bold');
        console.log(cleanData.cleanedTranscript);
        const rawWords = rawTranscriptString.split(/\s+/).length;
        const cleanWords = (cleanData.cleanedTranscript || '').split(/\s+/).length;
        const rawTurns = rawParsed.length;
        const cleanTurns = (cleanData.cleanedTranscript || '').split('\n').filter(l => l.trim() && (l.includes(facultyName + ':') || l.includes(traineeName + ':'))).length;
        console.log(`%cStats: raw=${rawWords} words / ${rawTurns} turns | cleaned=${cleanWords} words / ${cleanTurns} turns | words removed=${rawWords - cleanWords}`, 'color: #6366f1');
        console.groupEnd();
        // ── END DEVELOPER LOG ────────────────────────────────────────────────

        if (cleanData.cleanedTranscript) {
          const cleanedParsed = parseCleanedTranscript(cleanData.cleanedTranscript, facultyName, traineeName);
          if (cleanedParsed.length > 0) {
            console.log('\u2713 Cleaned transcript applied —', cleanedParsed.length, 'turns (was', rawParsed.length, 'raw)');
            setTranscript(cleanedParsed);
          } else {
            console.warn('\u26a0\ufe0f Cleaned transcript parsed 0 turns — falling back to raw');
            setTranscript(rawParsed);
          }
        } else {
          setTranscript(rawParsed);
        }
      }

    } catch (err) {
      setError(`Transcription failed: ${err.message}`);
    } finally {
      setIsTranscribing(false);
      setTranscribingPhase('');
      // Release wake lock now that transcribing is fully complete
      if (wakeLockRef.current) { wakeLockRef.current.release().catch(() => {}); wakeLockRef.current = null; }
    }
  };

  const transcribeAudio = async (audioBlob, mimeType) => {
    const tokenRes = await fetch('/api/get-deepgram-token');
    const { key: apiKey } = await tokenRes.json();

    const params = new URLSearchParams({
      model: 'nova-2-medical',
      diarize: 'true',
      punctuate: 'true',
      smart_format: 'true',
      utterances: 'true',
      min_speakers: '2',
      max_speakers: '2',
    });

    console.log('Sending to Deepgram — blob size:', audioBlob.size, 'type:', mimeType);

    const response = await fetch(`https://api.deepgram.com/v1/listen?${params}`, {
      method: 'POST',
      headers: { 'Authorization': `Token ${apiKey}`, 'Content-Type': mimeType },
      body: audioBlob,
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`Deepgram error ${response.status}: ${errText}`);
    }
    const data = await response.json();

    // Log full response for debugging
    console.log('Deepgram response metadata:', JSON.stringify(data?.metadata));
    console.log('Deepgram utterances count:', data?.results?.utterances?.length);
    console.log('Deepgram words count:', data?.results?.channels?.[0]?.alternatives?.[0]?.words?.length);
    console.log('Deepgram transcript:', data?.results?.channels?.[0]?.alternatives?.[0]?.transcript?.slice(0, 200));

    const utterances = data?.results?.utterances;
    const parsed = [];

    if (utterances?.length > 0) {
      for (const utt of utterances) {
        const text = utt.transcript?.trim();
        if (!text) continue;
        const speakerLabel = (utt.speaker ?? 0) === 0 ? 'faculty' : 'trainee';
        const last = parsed[parsed.length - 1];
        if (last && last.speaker === speakerLabel) {
          last.text += ' ' + text;
        } else {
          const s = Math.floor(utt.start || 0);
          parsed.push({ speaker: speakerLabel, text, timestamp: `${String(Math.floor(s/60)).padStart(2,'0')}:${String(s%60).padStart(2,'0')}` });
        }
      }
    } else {
      const words = data?.results?.channels?.[0]?.alternatives?.[0]?.words || [];
      if (words.length === 0) {
        // Log the full raw response so we can diagnose
        console.error('Deepgram full response:', JSON.stringify(data));
        throw new Error('No transcript returned from Deepgram — check browser console for full response details');
      }
      for (const word of words) {
        const speakerLabel = (word.speaker ?? 0) === 0 ? 'faculty' : 'trainee';
        const last = parsed[parsed.length - 1];
        if (last && last.speaker === speakerLabel) {
          last.text += ' ' + word.punctuated_word;
        } else {
          const s = Math.floor(word.start || 0);
          parsed.push({ speaker: speakerLabel, text: word.punctuated_word, timestamp: `${String(Math.floor(s/60)).padStart(2,'0')}:${String(s%60).padStart(2,'0')}` });
        }
      }
    }
    return parsed; // caller (stopRecording) decides what to display
  };
  // ── Robust cleaned transcript parser ────────────────────────────────────────
  // Handles two formats Call 0 may return:
  //   Format A (inline):  "Linda Smith: some text here"
  //   Format B (split):   "Linda Smith:\n" followed by "some text here" on next line
  // Also handles fuzzy name matching (case-insensitive, trimmed)
  const parseCleanedTranscript = (cleaned, fName, tName) => {
    const lines = cleaned.split('\n');
    const parsed = [];
    const fLower = fName.trim().toLowerCase();
    const tLower = tName.trim().toLowerCase();
    let currentSpeaker = null;

    const isFaculty  = (s) => s.trim().toLowerCase() === fLower || s.trim().toLowerCase() === fLower + ':';
    const isTrainee  = (s) => s.trim().toLowerCase() === tLower || s.trim().toLowerCase() === tLower + ':';
    const isSpeakerLine = (line) => {
      const stripped = line.trim().replace(/:$/, '');
      return isFaculty(stripped) || isTrainee(stripped);
    };
    const speakerFromLine = (line) => {
      const stripped = line.trim().replace(/:$/, '');
      if (isFaculty(stripped)) return 'faculty';
      if (isTrainee(stripped)) return 'trainee';
      return null;
    };

    for (const raw of lines) {
      const line = raw.trim();
      if (!line) continue;

      // Check if line is "Name:" or "Name: text"
      const colonIdx = line.indexOf(':');
      if (colonIdx > 0) {
        const namePart = line.slice(0, colonIdx).trim();
        const textPart = line.slice(colonIdx + 1).trim();
        const nameIsF = namePart.toLowerCase() === fLower;
        const nameIsT = namePart.toLowerCase() === tLower;

        if (nameIsF || nameIsT) {
          currentSpeaker = nameIsF ? 'faculty' : 'trainee';
          if (textPart) {
            // Inline format: "Linda Smith: text here"
            const last = parsed[parsed.length - 1];
            if (last && last.speaker === currentSpeaker) {
              last.text += ' ' + textPart;
            } else {
              parsed.push({ speaker: currentSpeaker, text: textPart, timestamp: '' });
            }
          }
          // If no textPart, speaker label only — text comes on next line
          continue;
        }
      }

      // If we reach here, line is utterance content
      if (currentSpeaker && line) {
        const last = parsed[parsed.length - 1];
        if (last && last.speaker === currentSpeaker) {
          last.text += ' ' + line;
        } else {
          parsed.push({ speaker: currentSpeaker, text: line, timestamp: '' });
        }
      }
    }

    return parsed;
  };

  const generateSummary = async () => {
    setIsGeneratingSummary(true);
    setError(null);
    setSaveSuccess(false);

    try {
      // Transcript is already cleaned by Call 0 during transcription step
      // Send it directly to Calls 1-5 (no Call 0 here anymore)
      const cleanedTranscriptString = transcript.map(t =>
        `${t.speaker === 'faculty' ? facultyName : traineeName}: ${t.text}`
      ).join('\n\n');

      const response = await fetch('/api/generate-summary', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transcript: cleanedTranscriptString,
          facultyName,
          facultyPronouns,
          traineeName,
          traineePronouns,
          trainingLevel
        })
      });

      if (!response.ok) throw new Error('Failed to generate summary');

      const data = await response.json();
      setEnhancedSummary(data.summary);

      // Seed editable narrative fields with AI output
      setEditedPlusNarrative(data.summary?.residentEvaluation?.plusSummary?.narrative || '');
      setEditedDeltaNarrative(data.summary?.residentEvaluation?.deltaSummary?.narrative || '');

      setShowSummary(true);
      setIsFinalized(false);

      // Switch to resident evaluation tab
      setActiveTab('resident');
    } catch (err) {
      console.error('Error generating summary:', err);
      setError(`Failed to generate summary: ${err.message}`);
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  const saveSessionToDatabase = async () => {
    setIsSaving(true);
    setSaveSuccess(false);
    try {
      // Merge edited narratives back into the summary object before saving
      const summaryToSave = enhancedSummary ? {
        ...enhancedSummary,
        residentEvaluation: {
          ...enhancedSummary.residentEvaluation,
          plusSummary: {
            ...enhancedSummary.residentEvaluation?.plusSummary,
            narrative: editedPlusNarrative
          },
          deltaSummary: {
            ...enhancedSummary.residentEvaluation?.deltaSummary,
            narrative: editedDeltaNarrative
          }
        }
      } : null;

      await fetch('/api/save-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          faculty_name: facultyName,
          trainee_name: traineeName,
          training_level: trainingLevel,
          session_date: new Date().toISOString(),
          transcript: transcript,
          summary: summaryToSave
        })
      });
      console.log('Session saved to database');
      setIsFinalized(true);
      setSaveSuccess(true);
      // Clear success message after 4 seconds
      setTimeout(() => setSaveSuccess(false), 4000);
    } catch (error) {
      console.error('Failed to save session:', error);
      setError('Failed to save session. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
      console.log('Copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const emailReport = async () => {
    try {
      if (!window.jspdf) {
        await new Promise((resolve, reject) => {
          const s = document.createElement('script');
          s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
          s.onload = resolve; s.onerror = reject;
          document.head.appendChild(s);
        });
      }

      const { jsPDF } = window.jspdf;
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });

      const dateStr = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      const ev = enhancedSummary?.residentEvaluation;
      const fd = enhancedSummary?.facultyDevelopment;
      const score = fd?.r2c2Score || 0;
      const scoreFill  = score >= 85 ? [220,252,231] : score >= 70 ? [254,243,199] : [254,226,226];
      const scoreText  = score >= 85 ? [20,83,45]    : score >= 70 ? [113,63,18]   : [153,27,27];
      const scoreLabel = score >= 85 ? 'Excellent'   : score >= 70 ? 'Good'        : 'Room for Growth';

      const ML_NAMES = {
        PC1:'Patient Care 1 — History', PC2:'Patient Care 2 — Physical Examination',
        PC3:'Patient Care 3 — Clinical Reasoning', PC4:'Patient Care 4 — Patient Management (Inpatient)',
        PC5:'Patient Care 5 — Patient Management (Outpatient)', PC6:'Patient Care 6 — Digital Health',
        MK1:'Medical Knowledge 1 — Applied Foundational Sciences', MK2:'Medical Knowledge 2 — Therapeutic Knowledge',
        MK3:'Medical Knowledge 3 — Diagnostic Testing',
        SBP1:'Systems-Based Practice 1 — Patient Safety & QI',
        SBP2:'Systems-Based Practice 2 — System Navigation',
        SBP3:'Systems-Based Practice 3 — Physician Role in Health Systems',
        PBLI1:'Practice-Based Learning 1 — Evidence-Based Practice',
        PBLI2:'Practice-Based Learning 2 — Reflective Practice & Personal Growth',
        PROF1:'Professionalism 1 — Professional Behavior', PROF2:'Professionalism 2 — Ethical Principles',
        PROF3:'Professionalism 3 — Accountability', PROF4:'Professionalism 4 — Well-Being',
        ICS1:'Communication 1 — Patient & Family-Centered',
        ICS2:'Communication 2 — Interprofessional & Team',
        ICS3:'Communication 3 — Health Care Systems',
      };

      // ── Geometry ──────────────────────────────────────────────────────────
      const PW = 215.9, PH = 279.4;
      const LM = 20, CW = PW - 40;
      const FOOTER_H = 14;
      let y = 0;

      const needSpace = (mm) => {
        if (y + mm > PH - FOOTER_H) { doc.addPage(); y = LM; }
      };

      // ── Helpers ───────────────────────────────────────────────────────────
      const tw = (txt, sz) => { doc.setFontSize(sz); return doc.getTextWidth(String(txt)); };

      const prose = (text, fontSize, colorRGB, leading) => {
        const fs = fontSize || 9.5;
        const col = colorRGB || [55,55,52];
        const lh = leading || 5.2;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(fs);
        doc.setTextColor(...col);
        const lines = doc.splitTextToSize(String(text || ''), CW);
        lines.forEach(line => { needSpace(lh + 1); doc.text(line, LM, y); y += lh; });
      };

      const eyebrow = (text) => {
        needSpace(7);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(160,158,150);
        doc.setCharSpace(1.2);
        doc.text(text.toUpperCase(), LM, y);
        doc.setCharSpace(0);
        y += 5;
      };

      const sp = (mm) => { y += (mm || 5); };

      const rule = () => {
        needSpace(4);
        doc.setDrawColor(220,220,215);
        doc.setLineWidth(0.25);
        doc.line(LM, y, LM + CW, y);
        y += 5;
      };

      const stampFooter = (pg, total) => {
        doc.setPage(pg);
        const fy = PH - 8;
        doc.setFillColor(248,247,244);
        doc.rect(0, PH - FOOTER_H, PW, FOOTER_H, 'F');
        doc.setDrawColor(220,220,215);
        doc.setLineWidth(0.25);
        doc.line(LM, PH - FOOTER_H, LM + CW, PH - FOOTER_H);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.setTextColor(160,158,150);
        doc.text('Synapse Feedback', LM, fy);
        doc.text(dateStr, LM + CW / 2, fy, { align: 'center' });
        doc.text('Page ' + pg + ' of ' + total, LM + CW, fy, { align: 'right' });
      };

      const drawPageHeader = (isFirst) => {
        if (isFirst) {
          doc.setFillColor(15,23,42);
          doc.rect(0, 0, PW, 38, 'F');
          doc.setFillColor(99,102,241);
          doc.rect(0, 0, PW, 1.2, 'F');
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(7.5);
          doc.setTextColor(99,102,241);
          doc.setCharSpace(2.5);
          doc.text('SYNAPSE FEEDBACK', LM, 9);
          doc.setCharSpace(0);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(19);
          doc.setTextColor(248,248,246);
          doc.text('Medical Feedback Report', LM, 20);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8.5);
          doc.setTextColor(148,163,184);
          doc.text(facultyName + '  —  ' + traineeName + '  (' + trainingLevel + ')  |  ' + dateStr, LM, 30);
          y = 48;
        } else {
          doc.setFillColor(248,247,244);
          doc.rect(0, 0, PW, 12, 'F');
          doc.setFillColor(99,102,241);
          doc.rect(0, 0, PW, 0.8, 'F');
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(7.5);
          doc.setTextColor(99,102,241);
          doc.setCharSpace(1.5);
          doc.text('SYNAPSE FEEDBACK', LM, 8);
          doc.setCharSpace(0);
          doc.setTextColor(160,158,150);
          doc.text(facultyName + ' to ' + traineeName, LM + CW, 8, { align: 'right' });
          y = 18;
        }
      };

      const sectionTitle = (text) => {
        needSpace(10);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(30,27,75);
        doc.setCharSpace(0.4);
        doc.text(text.toUpperCase(), LM, y);
        doc.setCharSpace(0);
        y += 2;
        doc.setDrawColor(199,210,254);
        doc.setLineWidth(0.5);
        doc.line(LM, y, LM + CW, y);
        y += 6;
      };

      const epaCard = (epa, isPrimary) => {
        const nameLines = doc.splitTextToSize(epa.number + ': ' + epa.name, CW - 12);
        const ctxLines  = doc.splitTextToSize(epa.clinicalContext || '', CW - 12);
        const boxH      = nameLines.length * 5.5 + ctxLines.length * 4.5 + 18;
        needSpace(boxH + 3);
        const fill   = isPrimary ? [238,242,255] : [249,250,251];
        const border = isPrimary ? [199,210,254] : [229,231,235];
        const acc    = isPrimary ? [79,70,229]   : [156,163,175];
        const nameC  = isPrimary ? [30,27,75]    : [31,41,55];
        const ctxC   = isPrimary ? [99,90,190]   : [107,114,128];
        doc.setFillColor(...fill);
        doc.setDrawColor(...border);
        doc.setLineWidth(0.25);
        doc.roundedRect(LM, y, CW, boxH, 2, 2, 'FD');
        doc.setFillColor(...acc);
        doc.rect(LM, y, 3.5, boxH, 'F');
        let iy = y + 6;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(isPrimary ? 10 : 9);
        doc.setTextColor(...nameC);
        nameLines.forEach(l => { doc.text(l, LM + 9, iy); iy += 5.5; });
        iy += 2;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(...acc);
        const badge = '[' + (epa.feedbackType || '') + ']';
        doc.text(badge, LM + 9, iy);
        const bw = tw(badge, 7);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...ctxC);
        const ctxF = doc.splitTextToSize(epa.clinicalContext || '', CW - 14 - bw);
        doc.text(ctxF[0] || '', LM + 9 + bw + 3, iy);
        iy += 5;
        ctxF.slice(1).forEach(l => { doc.text(l, LM + 9, iy); iy += 4.5; });
        y += boxH + 3.5;
      };

      const quoteBlock = (quotes, fill, border, textColor) => {
        if (!quotes || !quotes.length) return;
        sp(2);
        eyebrow('Supporting Evidence');
        quotes.forEach(q => {
          const qLines = doc.splitTextToSize('"' + q + '"', CW - 14);
          const qH = qLines.length * 4.5 + 6;
          needSpace(qH + 2);
          doc.setFillColor(...fill);
          doc.roundedRect(LM + 3, y - 0.5, CW - 3, qH, 1.5, 1.5, 'F');
          doc.setFillColor(...border);
          doc.rect(LM + 3, y - 0.5, 2.5, qH, 'F');
          doc.setFont('helvetica', 'italic');
          doc.setFontSize(8.5);
          doc.setTextColor(...textColor);
          let qy = y + 4;
          qLines.forEach(l => { doc.text(l, LM + 9, qy); qy += 4.5; });
          y += qH + 3;
        });
      };

      const narrativeSection = (title, narrative, quotes, fillRGB, borderRGB, textRGB, bodyColor) => {
        needSpace(16);
        doc.setFillColor(...fillRGB);
        doc.setDrawColor(...borderRGB);
        doc.setLineWidth(0.25);
        doc.roundedRect(LM, y, CW, 9.5, 1.5, 1.5, 'FD');
        doc.setFillColor(...textRGB);
        doc.rect(LM, y, 3.5, 9.5, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(...textRGB);
        doc.text(title, LM + 8, y + 6.3);
        y += 13;
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(7.5);
        doc.setTextColor(160,158,150);
        doc.text('AI-generated — reviewed and edited by attending before sending', LM, y);
        y += 5.5;
        prose(narrative, 9.5, bodyColor, 5.2);
        quoteBlock(quotes, fillRGB, borderRGB, textRGB);
        sp(6);
      };

      // ═══════════════════════════════════════════════
      // PAGE 1 — RESIDENT EVALUATION
      // ═══════════════════════════════════════════════
      drawPageHeader(true);
      sectionTitle('Resident Evaluation');

      narrativeSection(
        'What the Resident Did Well',
        editedPlusNarrative || (ev && ev.plusSummary && ev.plusSummary.narrative) || '',
        ev && ev.plusSummary && ev.plusSummary.evidenceQuotes,
        [240,253,244], [134,239,172], [21,128,61], [40,80,50]
      );

      narrativeSection(
        'Growth Opportunities',
        editedDeltaNarrative || (ev && ev.deltaSummary && ev.deltaSummary.narrative) || '',
        ev && ev.deltaSummary && ev.deltaSummary.evidenceQuotes,
        [255,251,235], [252,211,77], [146,64,14], [100,60,20]
      );

      if (ev && ev.acgmeMilestones && ev.acgmeMilestones.length > 0) {
        needSpace(14);
        eyebrow('ACGME IM 2.0 Competencies Addressed');
        sp(1);
        const colW = (CW - 6) / 2;
        let leftY = y, rightY = y;
        ev.acgmeMilestones.forEach((m, i) => {
          const x = i % 2 === 0 ? LM : LM + colW + 6;
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8.5);
          doc.setTextColor(60,60,58);
          const mLines = doc.splitTextToSize('\u2013  ' + (ML_NAMES[m] || m), colW);
          mLines.forEach(l => {
            if (i % 2 === 0) { doc.text(l, x, leftY); leftY += 4.5; }
            else { doc.text(l, x, rightY); rightY += 4.5; }
          });
        });
        y = Math.max(leftY, rightY) + 3;
      }

      rule();

      if (ev && ev.primaryEPA) {
        eyebrow('Primary EPA');
        epaCard(ev.primaryEPA, true);
        sp(2);
      }

      if (ev && ev.secondaryEPAs && ev.secondaryEPAs.length > 0) {
        sp(2);
        eyebrow('Additional EPAs Addressed');
        ev.secondaryEPAs.forEach(epa => epaCard(epa, false));
        sp(2);
      }

      // ═══════════════════════════════════════════════
      // PAGE 2 — FACULTY DEVELOPMENT
      // ═══════════════════════════════════════════════
      doc.addPage();
      drawPageHeader(false);
      sectionTitle('Faculty Development');

      // ── R2C2 Score block ────────────────────────────────────────────────────
      needSpace(28);
      doc.setFillColor(...scoreFill);
      doc.setDrawColor(220,220,215);
      doc.setLineWidth(0.25);
      doc.roundedRect(LM, y, CW, 26, 2, 2, 'FD');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(40);
      doc.setTextColor(...scoreText);
      doc.text(score + '%', LM + 10, y + 19);
      doc.setDrawColor(220,220,215);
      doc.setLineWidth(0.3);
      doc.line(LM + 44, y + 4, LM + 44, y + 22);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(30,27,75);
      doc.text('R2C2 Adherence Score', LM + 50, y + 9);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(120,120,112);
      doc.text('Target: >85% to meet standard', LM + 50, y + 15);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(...scoreText);
      doc.text(scoreLabel, LM + 50, y + 22);
      y += 31;

      // ── Key Insight ──────────────────────────────────────────────────────────
      if (fd && fd.keyInsight) {
        needSpace(16);
        eyebrow('Key Insight');
        const insLines = doc.splitTextToSize(fd.keyInsight, CW - 10);
        const insH = insLines.length * 5 + 8;
        doc.setFillColor(248,247,244);
        doc.setDrawColor(220,220,215);
        doc.setLineWidth(0.25);
        doc.roundedRect(LM, y, CW, insH, 2, 2, 'FD');
        doc.setFillColor(99,102,241);
        doc.rect(LM, y, 3, insH, 'F');
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9.5);
        doc.setTextColor(55,55,52);
        let ky = y + 5.5;
        insLines.forEach(l => { doc.text(l, LM + 8, ky); ky += 5; });
        y += insH + 6;
      }

      // ── What Worked Well + Opportunity — two column ─────────────────────────
      if (fd && (fd.whatWorkedWell || fd.opportunityToStrengthen)) {
        needSpace(24);
        const halfW = (CW - 4) / 2;

        // What Worked Well — left column
        const wwLines = doc.splitTextToSize(fd.whatWorkedWell || '', halfW - 8);
        const wwH = wwLines.length * 4.5 + 12;
        doc.setFillColor(240,253,244);
        doc.setDrawColor(134,239,172);
        doc.setLineWidth(0.25);
        doc.roundedRect(LM, y, halfW, wwH, 2, 2, 'FD');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(160,158,150);
        doc.setCharSpace(0.8);
        doc.text('WHAT WORKED WELL', LM + 5, y + 5.5);
        doc.setCharSpace(0);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.setTextColor(21,128,61);
        let wy = y + 10;
        wwLines.forEach(l => { doc.text(l, LM + 5, wy); wy += 4.5; });

        // Opportunity to Strengthen — right column
        const otLines = doc.splitTextToSize(fd.opportunityToStrengthen || '', halfW - 8);
        const otH = otLines.length * 4.5 + 12;
        const rx = LM + halfW + 4;
        doc.setFillColor(255,251,235);
        doc.setDrawColor(252,211,77);
        doc.setLineWidth(0.25);
        doc.roundedRect(rx, y, halfW, Math.max(wwH, otH), 2, 2, 'FD');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(160,158,150);
        doc.setCharSpace(0.8);
        doc.text('OPPORTUNITY', rx + 5, y + 5.5);
        doc.setCharSpace(0);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.setTextColor(146,64,14);
        let oy = y + 10;
        otLines.forEach(l => { doc.text(l, rx + 5, oy); oy += 4.5; });

        y += Math.max(wwH, otH) + 5;
      }

      // ── Coaching Tips ────────────────────────────────────────────────────────
      if (fd && fd.coachingTips && fd.coachingTips.length > 0) {
        needSpace(16);
        eyebrow('Try This Next Time');
        fd.coachingTips.forEach(function(tip, i) {
          const tipLines = doc.splitTextToSize(tip, CW - 14);
          const tipH = tipLines.length * 4.5 + 6;
          needSpace(tipH + 3);
          doc.setFillColor(248,247,244);
          doc.setDrawColor(220,220,215);
          doc.setLineWidth(0.25);
          doc.roundedRect(LM, y, CW, tipH, 2, 2, 'FD');
          // Number circle
          doc.setFillColor(99,102,241);
          doc.circle(LM + 7, y + tipH/2, 4, 'F');
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(7);
          doc.setTextColor(255,255,255);
          doc.text(String(i + 1), LM + 7, y + tipH/2 + 2.5, { align: 'center' });
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8.5);
          doc.setTextColor(55,55,52);
          let ty = y + 4.5;
          tipLines.forEach(l => { doc.text(l, LM + 14, ty); ty += 4.5; });
          y += tipH + 3;
        });
        sp(3);
      }

      // ── Sentiment inline ─────────────────────────────────────────────────────
      if (fd && fd.transcriptSentiment) {
        needSpace(8);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.setTextColor(120,120,112);
        doc.text('Transcript Sentiment:', LM, y);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(55,55,52);
        doc.text(fd.transcriptSentiment, LM + tw('Transcript Sentiment:', 8.5) + 2, y);
        y += 9;
      }

      rule();

      // ── Phase-by-Phase Analysis ──────────────────────────────────────────────
      needSpace(8);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(55,55,52);
      doc.setCharSpace(0.2);
      doc.text('Phase-by-Phase Analysis', LM, y);
      doc.setCharSpace(0);
      y += 7;

      const phases = [
        { key:'relationship', num:'1', name:'Rapport',      fill:[239,246,255], border:[191,219,254], acc:[37,99,235],  tc:[30,58,138]  },
        { key:'reaction',     num:'2', name:'Reaction',     fill:[240,253,244], border:[134,239,172], acc:[22,163,74],  tc:[20,83,45]   },
        { key:'content',      num:'3', name:'Content',      fill:[255,251,235], border:[252,211,77],  acc:[217,119,6],  tc:[120,53,15]  },
        { key:'coaching',     num:'4', name:'Coaching',     fill:[254,242,242], border:[252,165,165], acc:[220,38,38],  tc:[153,27,27]  },
      ];

      phases.forEach(function(ph) {
        const phase = fd && fd.phaseBreakdown && fd.phaseBreakdown[ph.key];
        const status     = (phase && phase.status) || 'Unknown';
        const labelText  = (phase && phase.label)  || status;
        const evidence   = (phase && phase.evidence)  || 'None observed';
        const suggestion = (phase && phase.delta)     || 'Continue current approach';
        doc.setFontSize(8.5);
        const evL = doc.splitTextToSize(evidence,   CW - 38);
        const suL = doc.splitTextToSize(suggestion, CW - 38);
        const boxH = (evL.length + suL.length) * 4.8 + 22;
        needSpace(boxH + 4);
        doc.setFillColor(...ph.fill);
        doc.setDrawColor(...ph.border);
        doc.setLineWidth(0.25);
        doc.roundedRect(LM, y, CW, boxH, 2, 2, 'FD');
        doc.setFillColor(...ph.acc);
        doc.rect(LM, y, 3.5, boxH, 'F');
        // Phase name
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9.5);
        doc.setTextColor(...ph.tc);
        doc.text(ph.num + '.  ' + ph.name, LM + 8, y + 7);
        // Status — plain text, no emoji
        const nameW = tw(ph.num + '.  ' + ph.name, 9.5);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...ph.acc);
        doc.text(labelText, LM + 8 + nameW + 4, y + 7);
        // Divider
        doc.setDrawColor(...ph.border);
        doc.setLineWidth(0.2);
        doc.line(LM + 3.5, y + 10, LM + CW - 0.25, y + 10);
        let iy = y + 16;
        // Evidence
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(160,158,150);
        doc.setCharSpace(0.8);
        doc.text('EVIDENCE', LM + 8, iy);
        doc.setCharSpace(0);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.setTextColor(55,55,52);
        evL.forEach(l => { doc.text(l, LM + 34, iy); iy += 4.8; });
        iy += 2;
        // Suggestion
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(160,158,150);
        doc.setCharSpace(0.8);
        doc.text('SUGGESTION', LM + 8, iy);
        doc.setCharSpace(0);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.setTextColor(55,55,52);
        suL.forEach(l => { doc.text(l, LM + 34, iy); iy += 4.8; });
        y += boxH + 5;
      });

      // Footers
      const total = doc.getNumberOfPages();
      for (let p = 1; p <= total; p++) stampFooter(p, total);

      const filename = 'SynapseFeedback-' + traineeName.replace(/\s+/g, '-') + '-' + new Date().toISOString().split('T')[0] + '.pdf';
      doc.save(filename);

      setTimeout(() => {
        const subject = encodeURIComponent('Synapse Feedback Report — ' + traineeName + ' — ' + dateStr);
        const body    = encodeURIComponent('Please find the attached Synapse Feedback report for ' + traineeName + ' (' + trainingLevel + ').\n\nGenerated on ' + dateStr + '.\n\nPlease attach the downloaded PDF to this email before sending.');
        window.location.href = 'mailto:?subject=' + subject + '&body=' + body;
      }, 1000);

    } catch (err) {
      setError('Failed to generate report: ' + err.message);
    }
  };

  // Attest & Share: saves edited narratives to Supabase AND generates PDF + opens email
  const attestAndShare = async () => {
    setError(null);
    try {
      await saveSessionToDatabase();
      await emailReport();
      setShowAttestModal(false);
    } catch (err) {
      console.error('Attest & share failed:', err);
      setError('Unable to complete attest & share. Please try again.');
    }
  };


  // ── Landing Page ──────────────────────────────────────────────────────────
  if (!sessionStarted && !enrollmentStep) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-8 flex items-center justify-center">
        <div className="max-w-md w-full mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
            <div className="text-center mb-6 sm:mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-indigo-600 rounded-full mb-4">
                <Mic className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Medical Feedback</h1>
              <p className="text-sm sm:text-base text-gray-600">AI-Powered Feedback Documentation</p>
            </div>
            <div className="space-y-4 sm:space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Who is giving feedback?</label>
                <input type="text" value={facultyName} onChange={(e) => setFacultyName(e.target.value)} placeholder="Dr. Smith"
                  className="w-full px-4 py-3 sm:py-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-base sm:text-lg" />
                <div className="flex gap-2 mt-2">
                  {['He/Him', 'She/Her', 'They/Them'].map(p => (
                    <button key={p} type="button" onClick={() => setFacultyPronouns(p)}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${facultyPronouns === p ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'}`}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Who is receiving feedback?</label>
                <input type="text" value={traineeName} onChange={(e) => setTraineeName(e.target.value)} placeholder="Dr. Johnson"
                  className="w-full px-4 py-3 sm:py-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-base sm:text-lg" />
                <div className="flex gap-2 mt-2">
                  {['He/Him', 'She/Her', 'They/Them'].map(p => (
                    <button key={p} type="button" onClick={() => setTraineePronouns(p)}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${traineePronouns === p ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'}`}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Training Level</label>
                <select value={trainingLevel} onChange={(e) => setTrainingLevel(e.target.value)}
                  className="w-full px-4 py-3 sm:py-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-base sm:text-lg bg-white">
                  <option value="">Select level...</option>
                  <option value="Medical Student">Medical Student</option>
                  <option value="Sub-intern">Sub-intern</option>
                  <option value="PGY-1">PGY-1</option>
                  <option value="PGY-2">PGY-2</option>
                  <option value="PGY-3">PGY-3</option>
                  <option value="Chief Resident">Chief Resident</option>
                  <option value="Fellow">Fellow</option>
                </select>
              </div>
              <button onClick={startSession}
                disabled={!facultyName || !traineeName || !trainingLevel || !facultyPronouns || !traineePronouns}
                className="w-full bg-indigo-600 text-white py-4 sm:py-5 rounded-lg text-base sm:text-lg font-semibold hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-lg">
                Begin Session →
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Enrollment Screens ────────────────────────────────────────────────────
  if (enrollmentStep) {
    const initials = (name) => name.trim().split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

    if (enrollmentStep === 'overview') {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-8 flex items-center justify-center">
          <div className="max-w-md w-full mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-600 rounded-full mb-4">
                  <Mic className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">Brief Voice Introductions</h2>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Each person will read one short sentence aloud. This takes about 10 seconds each and helps the AI correctly identify who is speaking throughout the session.
                </p>
              </div>
              <button onClick={() => setEnrollmentStep('faculty')}
                className="w-full bg-indigo-600 text-white py-4 rounded-xl text-base font-semibold hover:bg-indigo-700 transition-colors shadow-lg">
                Begin Introductions →
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (enrollmentStep === 'faculty') {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-8 flex items-center justify-center">
          <div className="max-w-md w-full mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-6">
                <div className="flex-1 h-1.5 bg-indigo-600 rounded-full" />
                <div className="flex-1 h-1.5 bg-gray-200 rounded-full" />
                <p className="text-xs text-gray-400 ml-1">1 of 2</p>
              </div>
              <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-200 rounded-xl p-3 mb-5">
                <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">{initials(facultyName)}</div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{facultyName}</p>
                  <p className="text-xs text-gray-500">Faculty · Attending Physician</p>
                </div>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-xl overflow-hidden mb-5">
                <div className="px-4 py-2 border-b border-gray-200 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-gray-400" />
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Read aloud</span>
                </div>
                <div className="px-4 py-4">
                  <p className="text-base text-gray-800 leading-relaxed italic">
                    "Hi, I'm <span className="not-italic font-semibold bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded">{facultyName}</span>, the attending physician on service. I'll be giving feedback today."
                  </p>
                </div>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-5 flex flex-col items-center gap-3">
                <div className="relative w-16 h-16">
                  <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                    <circle cx="32" cy="32" r="28" fill="none" stroke="#e5e7eb" strokeWidth="2.5" />
                    <circle cx="32" cy="32" r="28" fill="none" stroke={isEnrollmentRecording ? '#4f46e5' : '#e5e7eb'}
                      strokeWidth="2.5" strokeDasharray="176"
                      strokeDashoffset={isEnrollmentRecording ? 176 - (176 * enrollmentCountdown / 6) : 176}
                      style={{ transition: 'stroke-dashoffset 0.9s linear' }} />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center text-gray-700 font-light text-lg">
                    {enrollmentDone.faculty ? '✓' : isEnrollmentRecording ? enrollmentCountdown : '6'}
                  </div>
                </div>
                <div className="flex items-center gap-0.5 h-10">
                  {waveformBars.map((height, i) => (
                    <div key={i} className="w-1.5 rounded-full transition-all duration-75"
                      style={{ height: `${height}px`, backgroundColor: isEnrollmentRecording && voiceActive ? `hsl(${220 + (height/48)*20}, 70%, ${55+(height/48)*15}%)` : '#d1d5db' }} />
                  ))}
                </div>
                {enrollmentDone.faculty && (
                  <p className="text-xs font-semibold text-green-600">Introduction captured</p>
                )}
              </div>
              <button
                onClick={() => { if (enrollmentDone.faculty) { setEnrollmentStep('trainee'); return; } if (!isEnrollmentRecording) startEnrollmentRecording('faculty'); }}
                disabled={isEnrollmentRecording}
                className={`w-full py-4 rounded-xl text-base font-semibold transition-colors shadow-lg flex items-center justify-center gap-2 ${enrollmentDone.faculty ? 'bg-green-600 text-white hover:bg-green-700' : isEnrollmentRecording ? 'bg-red-600 text-white cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}>
                {enrollmentDone.faculty ? '✓ Recorded — Continue' : isEnrollmentRecording ? 'Recording...' : <><Mic className="w-4 h-4" /> Record Introduction</>}
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (enrollmentStep === 'trainee') {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-8 flex items-center justify-center">
          <div className="max-w-md w-full mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-6">
                <div className="flex-1 h-1.5 bg-indigo-600 rounded-full" />
                <div className="flex-1 h-1.5 bg-indigo-600 rounded-full" />
                <p className="text-xs text-gray-400 ml-1">2 of 2</p>
              </div>
              <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-3 mb-5">
                <div className="w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">{initials(traineeName)}</div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{traineeName}</p>
                  <p className="text-xs text-gray-500">Trainee · {trainingLevel}</p>
                </div>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-xl overflow-hidden mb-5">
                <div className="px-4 py-2 border-b border-gray-200 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-gray-400" />
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Read aloud</span>
                </div>
                <div className="px-4 py-4">
                  <p className="text-base text-gray-800 leading-relaxed italic">
                    "Hi, I'm <span className="not-italic font-semibold bg-green-100 text-green-800 px-1.5 py-0.5 rounded">{traineeName}</span>, a <span className="not-italic font-semibold bg-green-100 text-green-800 px-1.5 py-0.5 rounded">{trainingLevel}</span>. I'll be receiving feedback today."
                  </p>
                </div>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-5 flex flex-col items-center gap-3">
                <div className="relative w-16 h-16">
                  <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                    <circle cx="32" cy="32" r="28" fill="none" stroke="#e5e7eb" strokeWidth="2.5" />
                    <circle cx="32" cy="32" r="28" fill="none" stroke={isEnrollmentRecording ? '#16a34a' : '#e5e7eb'}
                      strokeWidth="2.5" strokeDasharray="176"
                      strokeDashoffset={isEnrollmentRecording ? 176 - (176 * enrollmentCountdown / 6) : 176}
                      style={{ transition: 'stroke-dashoffset 0.9s linear' }} />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center text-gray-700 font-light text-lg">
                    {enrollmentDone.trainee ? '✓' : isEnrollmentRecording ? enrollmentCountdown : '6'}
                  </div>
                </div>
                <div className="flex items-center gap-0.5 h-10">
                  {waveformBars.map((height, i) => (
                    <div key={i} className="w-1.5 rounded-full transition-all duration-75"
                      style={{ height: `${height}px`, backgroundColor: isEnrollmentRecording && voiceActive ? `hsl(${220 + (height/48)*20}, 70%, ${55+(height/48)*15}%)` : '#d1d5db' }} />
                  ))}
                </div>
                {enrollmentDone.trainee && (
                  <p className="text-xs font-semibold text-green-600">Introduction captured</p>
                )}
              </div>
              <button
                onClick={() => { if (enrollmentDone.trainee) { setEnrollmentStep(null); setSessionStarted(true); setTimeout(() => startRecording(), 300); return; } if (!isEnrollmentRecording) startEnrollmentRecording('trainee'); }}
                disabled={isEnrollmentRecording}
                className={`w-full py-4 rounded-xl text-base font-semibold transition-colors shadow-lg flex items-center justify-center gap-2 ${enrollmentDone.trainee ? 'bg-green-600 text-white hover:bg-green-700' : isEnrollmentRecording ? 'bg-red-600 text-white cursor-not-allowed' : 'bg-green-600 text-white hover:bg-green-700'}`}>
                {enrollmentDone.trainee ? '✓ Recorded — Continue' : isEnrollmentRecording ? 'Recording...' : <><Mic className="w-4 h-4" /> Record Introduction</>}
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  // ── Recording Session ──────────────────────────────────────────────────────
  const initials = (name) => name.trim().split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="sticky top-0 z-10 bg-white shadow-md">
        <div className="px-4 py-3 sm:px-6 sm:py-4">
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={() => { if (window.confirm('Return to home? This will clear the current session.')) { setSessionStarted(false); setTranscript([]); setShowSummary(false); } }}
              className="text-indigo-600 hover:text-indigo-700 font-medium text-sm sm:text-base">
              ← Back
            </button>
            <div className="text-xs sm:text-sm text-gray-500">{new Date().toLocaleDateString()}</div>
          </div>
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 text-center">Feedback Session</h2>
          <p className="text-xs sm:text-sm text-gray-600 text-center mt-1">
            <span className="font-medium">{facultyName}</span> → <span className="font-medium">{traineeName}</span> ({trainingLevel})
          </p>
        </div>

      </div>

      <div className="p-4 pb-20 max-w-4xl mx-auto">
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-t-lg shadow-md">
          <div className="flex border-b border-gray-200">
            {['transcript', 'resident', 'faculty'].map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`flex-1 py-4 px-3 text-center font-semibold text-sm sm:text-base transition-colors capitalize ${activeTab === tab ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`}>
                {tab}
                {tab === 'transcript' && isRecording && (
                  <span className="ml-2 inline-flex items-center gap-1">
                    <div className="w-2 h-2 bg-red-600 rounded-full animate-pulse" />
                    <span className="text-xs text-red-600">REC</span>
                  </span>
                )}
                {tab !== 'transcript' && showSummary && <span className="ml-2 text-green-600 text-xs">✓</span>}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-b-lg shadow-md">

          {/* ── Transcript Tab ── */}
          {activeTab === 'transcript' && (
            <div className="p-4 sm:p-6">
              <div className="mb-4 space-y-3">
                {!isRecording && !isTranscribing && (
                  <button onClick={startRecording}
                    className="w-full bg-red-600 text-white py-4 sm:py-5 rounded-lg text-base sm:text-lg font-semibold hover:bg-red-700 transition-colors flex items-center justify-center gap-2 shadow-lg">
                    <Mic className="w-5 h-5 sm:w-6 sm:h-6" /> Start Recording
                  </button>
                )}
                {isRecording && (
                  <button onClick={stopRecording}
                    className="w-full bg-gray-800 text-white py-4 sm:py-5 rounded-lg text-base sm:text-lg font-semibold hover:bg-gray-900 transition-colors flex items-center justify-center gap-2 shadow-lg">
                    <Square className="w-5 h-5 sm:w-6 sm:h-6" /> Stop Recording
                  </button>
                )}

                {isRecording && (
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex flex-col items-center gap-3">
                    <div className="flex items-center gap-3 w-full justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                        <span className="text-xs font-semibold text-red-500 tracking-wider">REC</span>
                      </div>
                      <span className="text-gray-500 text-xs font-mono">
                        {String(Math.floor(recordingDuration / 60)).padStart(2, '0')}:{String(recordingDuration % 60).padStart(2, '0')}
                      </span>
                    </div>
                    <div className="flex items-center gap-0.5 h-14">
                      {waveformBars.map((height, i) => (
                        <div key={i} className="w-1.5 rounded-full transition-all duration-75"
                          style={{ height: `${height}px`, backgroundColor: voiceActive ? `hsl(${220 + (height/48)*20}, 70%, ${55+(height/48)*15}%)` : '#d1d5db' }} />
                      ))}
                    </div>
                  </div>
                )}

                {isTranscribing && (
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
                    {/* Phase label */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                        <div>
                          <p className="text-gray-800 font-semibold text-sm">
                            {transcribingPhase === 'deepgram' ? 'Transcribing...' : 'Optimizing transcript...'}
                          </p>
                          <p className="text-gray-500 text-xs mt-0.5">
                            {transcribingPhase === 'deepgram'
                              ? 'Converting audio to text'
                              : 'AI is correcting & improving transcript'}
                          </p>
                        </div>
                      </div>
                      <span className="text-gray-500 text-xs font-mono">{Math.round(transcribingProgress)}%</span>
                    </div>
                    {/* Progress bar */}
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div className="bg-indigo-600 h-1.5 rounded-full transition-all duration-150" style={{ width: `${transcribingProgress}%` }} />
                    </div>
                    {/* Step indicators */}
                    <div className="flex items-center gap-2 pt-1">
                      <div className={`flex items-center gap-1.5 text-xs font-medium ${transcribingPhase === 'deepgram' ? 'text-indigo-600' : 'text-green-600'}`}>
                        {transcribingPhase === 'deepgram'
                          ? <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse inline-block" />
                          : <span className="text-green-600">✓</span>
                        }
                        Transcribing
                      </div>
                      <span className="text-gray-300">→</span>
                      <div className={`flex items-center gap-1.5 text-xs font-medium ${transcribingPhase === 'cleaning' ? 'text-indigo-600' : 'text-gray-400'}`}>
                        {transcribingPhase === 'cleaning' && <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse inline-block" />}
                        Optimizing
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {!isRecording && !isTranscribing && transcript.length === 0 && (
                  <p className="text-sm text-gray-500 text-center">
                    Introductions complete — you may start recording your feedback session now.
                  </p>
                )}

              <div className="border border-gray-200 rounded-lg p-3 sm:p-4 overflow-y-auto bg-gray-50" style={{ height: '50vh', minHeight: '300px' }}>
                {transcript.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-gray-400 px-4 text-center">
                    <p className="text-sm sm:text-base">Transcript will appear here after you stop the recording.</p>
                  </div>
                ) : (
                  <div className="space-y-3 sm:space-y-4">
                    {transcript.map((entry, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <div className={`flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center ${entry.speaker === 'faculty' ? 'bg-indigo-100' : 'bg-green-100'}`}>
                          <User className={`w-4 h-4 sm:w-5 sm:h-5 ${entry.speaker === 'faculty' ? 'text-indigo-600' : 'text-green-600'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs sm:text-sm font-semibold ${entry.speaker === 'faculty' ? 'text-indigo-600' : 'text-green-600'}`}>
                              {entry.speaker === 'faculty' ? facultyName : traineeName}
                            </span>
                            <span className="text-xs text-gray-400">{entry.timestamp}</span>
                          </div>
                          <p className="text-gray-700 text-sm sm:text-base break-words">{entry.text}</p>
                        </div>
                      </div>
                    ))}
                    <div ref={transcriptEndRef} />
                  </div>
                )}
              </div>

              {transcript.length > 0 && !isRecording && !isTranscribing && (
                <div className="mt-4 space-y-3">
                  <button onClick={generateSummary} disabled={isGeneratingSummary}
                    className="w-full bg-indigo-600 text-white py-4 sm:py-5 rounded-lg text-base sm:text-lg font-semibold hover:bg-indigo-700 disabled:bg-indigo-400 transition-colors flex items-center justify-center gap-2 shadow-lg">
                    <Sparkles className="w-5 h-5 sm:w-6 sm:h-6" />
                    {isGeneratingSummary ? 'Generating...' : 'Generate AI Summary'}
                  </button>
                  {isGeneratingSummary && (
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                          <span className="text-gray-800 font-semibold text-sm">Generating summary...</span>
                        </div>
                        <span className="text-gray-500 text-xs">{Math.round(generatingProgress)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div className="bg-indigo-600 h-1.5 rounded-full transition-all duration-150" style={{ width: `${generatingProgress}%` }} />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Resident Tab ── */}
          {activeTab === 'resident' && (
            <div className="p-4 sm:p-6">
              {!showSummary ? (
                <div className="flex flex-col items-center justify-center py-16 text-center text-gray-400">
                  <Sparkles className="w-16 h-16 mb-4 text-gray-300" />
                  <p className="text-sm sm:text-base">Complete recording and generate analysis<br />to view resident evaluation</p>
                </div>
              ) : (
                <div className="space-y-6">

                  {/* Plus Summary — tap to edit */}
                  <div className="bg-green-50 border-l-4 border-green-600 p-4 rounded-r-lg">
                    <div className="flex items-start justify-between mb-1 gap-2">
                      <h3 className="text-lg font-bold text-green-900">✨ What the Resident Did Well</h3>
                      <button
                        onClick={() => setPlusEditMode(!plusEditMode)}
                        className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${plusEditMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-white text-green-700 border border-green-300 hover:bg-green-100'}`}
                      >
                        {plusEditMode ? (<><Check className="w-3.5 h-3.5" />Done</>) : (<><Edit2 className="w-3.5 h-3.5" />Tap to edit</>)}
                      </button>
                    </div>
                    <p className="text-xs text-green-700 mb-3 italic">AI-generated — review and edit before attesting</p>
                    {enhancedSummary?.residentEvaluation?.plusSummary?.narrative && (
                      <div className="mb-4">
                        {plusEditMode ? (
                          <textarea value={editedPlusNarrative} onChange={(e) => setEditedPlusNarrative(e.target.value)} rows={7} autoFocus
                            className="w-full p-4 text-sm text-gray-800 leading-relaxed bg-white border-2 border-green-500 rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none resize-y" />
                        ) : (
                          <div className="bg-white p-4 rounded-lg border border-green-200">
                            <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{editedPlusNarrative}</p>
                          </div>
                        )}
                      </div>
                    )}
                    {enhancedSummary?.residentEvaluation?.plusSummary?.evidenceQuotes?.length > 0 && (
                      <div>
                        <p className="text-sm font-semibold text-green-900 mb-2">Supporting Evidence:</p>
                        <div className="space-y-2">
                          {enhancedSummary.residentEvaluation.plusSummary.evidenceQuotes.map((q, i) => (
                            <div key={i} className="bg-white p-3 rounded-lg border border-green-200">
                              <p className="text-sm text-gray-700 italic">• "{q}"</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Delta Summary — tap to edit */}
                  <div className="bg-amber-50 border-l-4 border-amber-600 p-4 rounded-r-lg">
                    <div className="flex items-start justify-between mb-1 gap-2">
                      <h3 className="text-lg font-bold text-amber-900">📈 Growth Opportunities</h3>
                      <button
                        onClick={() => setDeltaEditMode(!deltaEditMode)}
                        className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${deltaEditMode ? 'bg-amber-600 text-white hover:bg-amber-700' : 'bg-white text-amber-700 border border-amber-300 hover:bg-amber-100'}`}
                      >
                        {deltaEditMode ? (<><Check className="w-3.5 h-3.5" />Done</>) : (<><Edit2 className="w-3.5 h-3.5" />Tap to edit</>)}
                      </button>
                    </div>
                    <p className="text-xs text-amber-700 mb-3 italic">AI-generated — review and edit before attesting</p>
                    {enhancedSummary?.residentEvaluation?.deltaSummary?.narrative && (
                      <div className="mb-4">
                        {deltaEditMode ? (
                          <textarea value={editedDeltaNarrative} onChange={(e) => setEditedDeltaNarrative(e.target.value)} rows={7} autoFocus
                            className="w-full p-4 text-sm text-gray-800 leading-relaxed bg-white border-2 border-amber-500 rounded-lg focus:ring-2 focus:ring-amber-500 focus:outline-none resize-y" />
                        ) : (
                          <div className="bg-white p-4 rounded-lg border border-amber-200">
                            <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{editedDeltaNarrative}</p>
                          </div>
                        )}
                      </div>
                    )}
                    {enhancedSummary?.residentEvaluation?.deltaSummary?.evidenceQuotes?.length > 0 && (
                      <div>
                        <p className="text-sm font-semibold text-amber-900 mb-2">Supporting Evidence:</p>
                        <div className="space-y-2">
                          {enhancedSummary.residentEvaluation.deltaSummary.evidenceQuotes.map((q, i) => (
                            <div key={i} className="bg-white p-3 rounded-lg border border-amber-200">
                              <p className="text-sm text-gray-700 italic">• "{q}"</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ACGME Competencies */}
                  {(() => {
                    const ML = { PC1:'Patient Care 1: History', PC2:'Patient Care 2: Physical Examination', PC3:'Patient Care 3: Clinical Reasoning', PC4:'Patient Care 4: Patient Management – Inpatient', PC5:'Patient Care 5: Patient Management – Outpatient', PC6:'Patient Care 6: Digital Health', MK1:'Medical Knowledge 1: Applied Foundational Sciences', MK2:'Medical Knowledge 2: Therapeutic Knowledge', MK3:'Medical Knowledge 3: Knowledge of Diagnostic Testing', SBP1:'Systems-Based Practice 1: Patient Safety and Quality Improvement', SBP2:'Systems-Based Practice 2: System Navigation for Patient-Centered Care', SBP3:'Systems-Based Practice 3: Physician Role in Health Care Systems', PBLI1:'Practice-Based Learning 1: Evidence-Based and Informed Practice', PBLI2:'Practice-Based Learning 2: Reflective Practice and Commitment to Personal Growth', PROF1:'Professionalism 1: Professional Behavior', PROF2:'Professionalism 2: Ethical Principles', PROF3:'Professionalism 3: Accountability/Conscientiousness', PROF4:'Professionalism 4: Knowledge of Systemic and Individual Factors of Well-Being', ICS1:'Interpersonal & Communication Skills 1: Patient- and Family-Centered Communication', ICS2:'Interpersonal & Communication Skills 2: Interprofessional and Team Communication', ICS3:'Interpersonal & Communication Skills 3: Communication within Health Care Systems' };
                    return (
                      <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded-r-lg">
                        <h3 className="text-lg font-bold text-blue-900 mb-3">🏆 ACGME IM 2.0 Competencies Addressed</h3>
                        <div className="flex flex-wrap gap-2">
                          {enhancedSummary?.residentEvaluation?.acgmeMilestones?.length > 0 ? enhancedSummary.residentEvaluation.acgmeMilestones.map((m, i) => (
                            <span key={i} className="px-3 py-2 bg-blue-600 text-white rounded-full text-sm font-semibold shadow-md">{ML[m] || m}</span>
                          )) : <p className="text-sm text-gray-600 italic">No competencies mapped</p>}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Primary EPA */}
                  <div className="bg-indigo-50 border-l-4 border-indigo-600 p-4 rounded-r-lg">
                    <h3 className="text-lg font-bold text-indigo-900 mb-3">📋 Primary EPA</h3>
                    <p className="text-base text-indigo-800 font-semibold mb-2">
                      {enhancedSummary?.residentEvaluation?.primaryEPA?.number}: {enhancedSummary?.residentEvaluation?.primaryEPA?.name}
                    </p>
                    <p className="text-sm text-indigo-700">
                      [{enhancedSummary?.residentEvaluation?.primaryEPA?.feedbackType}] {enhancedSummary?.residentEvaluation?.primaryEPA?.clinicalContext}
                    </p>
                  </div>

                  {/* Secondary EPAs */}
                  {enhancedSummary?.residentEvaluation?.secondaryEPAs?.length > 0 && (
                    <div className="space-y-3">
                      <h3 className="text-lg font-bold text-gray-900">📎 Additional EPAs Addressed</h3>
                      {enhancedSummary.residentEvaluation.secondaryEPAs.map((epa, i) => (
                        <div key={i} className="bg-gray-50 border-l-4 border-gray-400 p-4 rounded-r-lg">
                          <p className="text-base font-semibold text-gray-900 mb-1">{epa.number}: {epa.name}</p>
                          <p className="text-sm text-gray-700">[{epa.feedbackType}] {epa.clinicalContext}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Save success toast */}
                  {saveSuccess && (
                    <div className="flex items-center gap-3 bg-green-50 border border-green-300 rounded-lg p-4">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-green-900">Attested and saved</p>
                        <p className="text-xs text-green-700">Report downloaded — share via your preferred app</p>
                      </div>
                    </div>
                  )}

                  {/* Attest & Share */}
                  <button
                    onClick={() => setShowAttestModal(true)}
                    disabled={isSaving || !enhancedSummary}
                    className="w-full bg-indigo-600 text-white py-4 rounded-lg text-lg font-semibold hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 shadow-lg"
                  >
                    {isSaving ? (
                      <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />Processing...</>
                    ) : (
                      <><Share2 className="w-6 h-6" />Attest &amp; Share</>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Faculty Tab ── */}
          {activeTab === 'faculty' && (
            <div className="p-4 sm:p-6">
              {!showSummary ? (
                <div className="flex flex-col items-center justify-center py-16 text-center text-gray-400">
                  <Sparkles className="w-16 h-16 mb-4 text-gray-300" />
                  <p className="text-sm sm:text-base">Complete recording and generate analysis<br />to view faculty coaching report</p>
                </div>
              ) : (
                <div className="space-y-6">

                  {/* R2C2 Score */}
                  <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6 rounded-xl shadow-xl">
                    <h3 className="text-2xl font-bold mb-2">R2C2 Adherence Score</h3>
                    <div className="flex items-center gap-4">
                      <div className="text-6xl font-black">{enhancedSummary?.facultyDevelopment?.r2c2Score || 0}%</div>
                      <div className="text-sm opacity-95">
                        <p className="mb-1">Target: &gt;85%</p>
                        <p className="font-bold text-lg">
                          {(enhancedSummary?.facultyDevelopment?.r2c2Score || 0) >= 85 ? 'Excellent' : (enhancedSummary?.facultyDevelopment?.r2c2Score || 0) >= 70 ? 'Good' : 'Room for Growth'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Key Insight */}
                  <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded-r-lg">
                    <h3 className="text-sm font-bold text-blue-900 mb-1 uppercase tracking-wide">Key Insight</h3>
                    <p className="text-sm text-blue-800 leading-relaxed">{enhancedSummary?.facultyDevelopment?.keyInsight || 'Analysis of feedback quality'}</p>
                  </div>

                  {/* What worked well + Opportunity */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h4 className="text-xs font-bold text-green-800 uppercase tracking-wide mb-2">What Worked Well</h4>
                      <p className="text-sm text-green-900 leading-relaxed">{enhancedSummary?.facultyDevelopment?.whatWorkedWell || '—'}</p>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <h4 className="text-xs font-bold text-amber-800 uppercase tracking-wide mb-2">Opportunity to Strengthen</h4>
                      <p className="text-sm text-amber-900 leading-relaxed">{enhancedSummary?.facultyDevelopment?.opportunityToStrengthen || '—'}</p>
                    </div>
                  </div>

                  {/* Coaching Tips */}
                  {enhancedSummary?.facultyDevelopment?.coachingTips?.length > 0 && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">Try This Next Time</h4>
                      <div className="space-y-2">
                        {enhancedSummary.facultyDevelopment.coachingTips.map((tip, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                            <p className="text-sm text-gray-700">{tip}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Sentiment */}
                  <div className="text-sm text-gray-500">
                    Transcript Sentiment: <span className="font-semibold text-gray-700">{enhancedSummary?.facultyDevelopment?.transcriptSentiment || 'Neutral'}</span>
                  </div>

                  {/* Phase breakdown */}
                  <div className="space-y-4">
                    <h3 className="text-base font-bold text-gray-900">Phase-by-Phase Analysis</h3>
                    {[
                      { key: 'relationship', num: '1', name: 'Rapport',   card: 'border-blue-300',  hdr: 'text-blue-900',  evBg: 'bg-blue-50',  evTxt: 'text-blue-700',  sBdr: 'border-blue-200',  sTxt: 'text-blue-800' },
                      { key: 'reaction',     num: '2', name: 'Reaction',  card: 'border-green-300', hdr: 'text-green-900', evBg: 'bg-green-50', evTxt: 'text-green-700', sBdr: 'border-green-200', sTxt: 'text-green-800' },
                      { key: 'content',      num: '3', name: 'Content',   card: 'border-amber-300', hdr: 'text-amber-900', evBg: 'bg-amber-50', evTxt: 'text-amber-700', sBdr: 'border-amber-200', sTxt: 'text-amber-800' },
                      { key: 'coaching',     num: '4', name: 'Coaching',  card: 'border-red-300',   hdr: 'text-red-900',   evBg: 'bg-red-50',   evTxt: 'text-red-700',   sBdr: 'border-red-200',   sTxt: 'text-red-800' },
                    ].map(({ key, num, name, card, hdr, evBg, evTxt, sBdr, sTxt }) => {
                      const phase = enhancedSummary?.facultyDevelopment?.phaseBreakdown?.[key];
                      const statusText = phase?.status || 'Unknown';
                      const statusColor = statusText === 'Met' ? 'text-green-600' : statusText === 'Partial' ? 'text-amber-600' : 'text-red-600';
                      return (
                        <div key={key} className={`bg-white border-2 ${card} rounded-lg overflow-hidden`}>
                          <div className={`flex items-center justify-between px-4 py-3 ${evBg}`}>
                            <h4 className={`font-bold ${hdr}`}>{num}. {name}</h4>
                            <div className="text-right">
                              <span className={`text-xs font-bold ${statusColor}`}>{statusText}</span>
                              {phase?.label && <p className="text-xs text-gray-500 mt-0.5">{phase.label}</p>}
                            </div>
                          </div>
                          <div className="p-4 space-y-3">
                            <div>
                              <p className={`text-xs font-semibold ${evTxt} mb-1 uppercase tracking-wide`}>Evidence</p>
                              <p className="text-sm text-gray-700 italic">{phase?.evidence || 'None observed'}</p>
                            </div>
                            <div className={`p-3 rounded-lg border ${sBdr} bg-white`}>
                              <p className={`text-xs font-semibold ${evTxt} mb-1 uppercase tracking-wide`}>Suggestion</p>
                              <p className={`text-sm ${sTxt}`}>{phase?.delta || 'Continue current approach'}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Save success toast */}
                  {saveSuccess && (
                    <div className="flex items-center gap-3 bg-green-50 border border-green-300 rounded-lg p-4">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-green-900">Attested and saved</p>
                        <p className="text-xs text-green-700">Report downloaded — share via your preferred app</p>
                      </div>
                    </div>
                  )}

                  {/* Attest & Share */}
                  <button
                    onClick={() => setShowAttestModal(true)}
                    disabled={isSaving || !enhancedSummary}
                    className="w-full bg-indigo-600 text-white py-4 rounded-lg text-lg font-semibold hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 shadow-lg"
                  >
                    {isSaving ? (
                      <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />Processing...</>
                    ) : (
                      <><Share2 className="w-6 h-6" />Attest &amp; Share</>
                    )}
                  </button>

                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Attestation Modal */}
      {showAttestModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-7">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                  <Share2 className="w-5 h-5 text-indigo-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Attest Evaluation</h3>
              </div>
              <button
                onClick={() => setShowAttestModal(false)}
                disabled={isSaving}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-gray-700 leading-relaxed mb-4">
              By attesting, you confirm that this evaluation accurately reflects your assessment of <span className="font-semibold">{traineeName}</span> based on the feedback session conducted on {new Date().toLocaleDateString()}.
            </p>

            <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 mb-5">
              <p className="text-xs text-indigo-900 leading-relaxed">
                This will save your edited narratives to the database and download the feedback report PDF, which you can share via email or any preferred app.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={() => setShowAttestModal(false)}
                disabled={isSaving}
                className="sm:flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={attestAndShare}
                disabled={isSaving}
                className="sm:flex-1 px-4 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 disabled:bg-gray-300"
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Attest &amp; Share
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MedicalFeedbackApp;
